/**
 * Ferris Wheel — animated procedural ride.
 */

import * as THREE from 'three';

export class FerrisWheel {
  static build(): { group: THREE.Group; update: (dt: number) => void } {
    const group = new THREE.Group();
    group.name = 'FerrisWheel';

    const matSteel = new THREE.MeshStandardMaterial({
      color: '#bcc4cc',
      roughness: 0.45,
      metalness: 0.8
    });
    const matSpoke = new THREE.MeshStandardMaterial({
      color: '#ddd6c8',
      roughness: 0.5,
      metalness: 0.7
    });
    const matCarBody = new THREE.MeshStandardMaterial({
      color: '#ff5f6d',
      roughness: 0.6
    });
    const matLight = new THREE.MeshStandardMaterial({
      color: '#ffc371',
      emissive: '#ffc371',
      emissiveIntensity: 1.4
    });

    const radius = 16;
    const hubH = radius + 1;

    // Two A-frame supports
    for (const sign of [-1, 1] as const) {
      const leg = new THREE.Mesh(
        new THREE.CylinderGeometry(0.35, 0.5, hubH * 1.1, 8),
        matSteel
      );
      leg.position.set(sign * 6, hubH / 2, 0);
      leg.rotation.z = sign * 0.2;
      leg.castShadow = true;
      group.add(leg);
    }

    // Hub
    const hub = new THREE.Mesh(
      new THREE.CylinderGeometry(0.8, 0.8, 1.2, 16),
      matSteel
    );
    hub.rotation.z = Math.PI / 2;
    hub.position.y = hubH;
    hub.castShadow = true;
    group.add(hub);

    // Wheel group (rotates)
    const wheel = new THREE.Group();
    wheel.position.y = hubH;
    group.add(wheel);

    // Outer rim — torus segments approximated by a torus
    const rim = new THREE.Mesh(
      new THREE.TorusGeometry(radius, 0.18, 8, 48),
      matSpoke
    );
    rim.rotation.y = Math.PI / 2;
    wheel.add(rim);
    const rim2 = rim.clone();
    rim2.position.x = 1.2;
    wheel.add(rim2);
    const rim3 = rim.clone();
    rim3.position.x = -1.2;
    wheel.add(rim3);

    // Spokes + cars
    const carCount = 12;
    const cars: { group: THREE.Group; offset: number }[] = [];
    for (let i = 0; i < carCount; i++) {
      const a = (i / carCount) * Math.PI * 2;
      // Spoke
      const spoke = new THREE.Mesh(
        new THREE.CylinderGeometry(0.08, 0.08, radius, 4),
        matSpoke
      );
      spoke.rotation.z = a + Math.PI / 2;
      spoke.position.set(0, 0, 0);
      const dx = Math.cos(a) * radius / 2;
      const dy = Math.sin(a) * radius / 2;
      spoke.position.set(0, dy, dx);
      // Rotate spoke to align with axis
      spoke.rotation.set(a, 0, Math.PI / 2);
      wheel.add(spoke);

      // Car at the end of the spoke
      const car = new THREE.Group();
      const body = new THREE.Mesh(
        new THREE.BoxGeometry(2, 1.2, 1.5),
        matCarBody
      );
      body.castShadow = true;
      car.add(body);
      // Top hanger
      const hanger = new THREE.Mesh(
        new THREE.CylinderGeometry(0.06, 0.06, 0.6, 4),
        matSpoke
      );
      hanger.position.y = 0.9;
      car.add(hanger);
      // Light
      const lamp = new THREE.Mesh(
        new THREE.SphereGeometry(0.1, 8, 6),
        matLight
      );
      lamp.position.y = 1.2;
      car.add(lamp);

      const carX = Math.cos(a) * radius;
      const carY = Math.sin(a) * radius;
      car.position.set(0, carY, carX);
      wheel.add(car);
      cars.push({ group: car, offset: a });
    }

    // Decorative ring lights on the rim
    for (let i = 0; i < 24; i++) {
      const a = (i / 24) * Math.PI * 2;
      const lamp = new THREE.Mesh(
        new THREE.SphereGeometry(0.08, 6, 4),
        matLight
      );
      lamp.position.set(0, Math.sin(a) * radius, Math.cos(a) * radius);
      wheel.add(lamp);
    }

    let t = 0;
    return {
      group,
      update: (dt: number) => {
        t += dt;
        wheel.rotation.x = t * 0.15;
        // Counter-rotate cars so they hang down
        for (const c of cars) {
          c.group.rotation.x = -wheel.rotation.x;
        }
      }
    };
  }
}
